import { Card } from '@cortex/web-design-system/components/ui/Card';
import { useTranslation } from 'react-i18next';
import type { BugEvaluationDetail } from '../../../lib/bug-evaluation-api';
import { TranslatedBadge } from './TranslatedBadge';

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business バグ評価結果の 9 項目（開発原因 / 改善フェーズ / 再発当否 / アンチパターン等）＋評価理由本文を描画する。
 * @graph-connects TranslatedBadge [calls] 翻訳済みバッジ
 */
export function EvaluationResultsCard({
  detail,
  isTranslated,
  showOriginal,
}: {
  detail: BugEvaluationDetail;
  isTranslated: boolean;
  showOriginal: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4">
        {t('bugEvaluation.evaluationResults', '評価結果')}
      </h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3 text-sm">
        <div>
          <span className="text-[var(--color-text-secondary)]">
            {t('bugEvaluation.implementer', '実装担当者（起因案件）')}:
          </span>{' '}
          {detail.implementer ?? t('bugEvaluation.implementerUnset', '未確定')}
        </div>
        <div>
          <span className="text-[var(--color-text-secondary)]">
            {t('bugEvaluation.devCause', '開発原因')}:
          </span>{' '}
          {detail.devCauseType}
        </div>
        <div>
          <span className="text-[var(--color-text-secondary)]">
            {t('bugEvaluation.improvementDev', '改善(開発)')}:
          </span>{' '}
          {detail.improvementPhaseDev}
        </div>
        <div>
          <span className="text-[var(--color-text-secondary)]">
            {t('bugEvaluation.testCause', 'テスト原因')}:
          </span>{' '}
          {detail.testCauseType}
        </div>
        <div>
          <span className="text-[var(--color-text-secondary)]">
            {t('bugEvaluation.improvementTest', '改善(テスト)')}:
          </span>{' '}
          {detail.improvementPhaseTest}
        </div>
        <div>
          <span className="text-[var(--color-text-secondary)]">
            {t('bugEvaluation.improvementReview', '改善(レビュー)')}:
          </span>{' '}
          {detail.improvementPhaseReview}
        </div>
        <div>
          <span className="text-[var(--color-text-secondary)]">
            {t('bugEvaluation.recurrence', '再発当否')}:
          </span>{' '}
          {detail.recurrence}
        </div>
        <div>
          <span className="text-[var(--color-text-secondary)]">
            {t('bugEvaluation.antiPattern', 'アンチパターン')}:
          </span>{' '}
          {detail.antiPattern}
        </div>
        <div>
          <span className="text-[var(--color-text-secondary)]">
            {t('bugEvaluation.irregular', 'イレギュラー')}:
          </span>{' '}
          {detail.irregular}
        </div>
        <div>
          <span className="text-[var(--color-text-secondary)]">
            {t('bugEvaluation.flowCompliance', 'フロー準拠')}:
          </span>{' '}
          {detail.devFlowCompliance}
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center mb-2">
          <span className="text-sm font-medium text-[var(--color-text-secondary)]">
            {t('bugEvaluation.evaluationReason', '評価理由')}
          </span>
          {isTranslated && !showOriginal && <TranslatedBadge />}
        </div>
        <div className="rounded-lg bg-[var(--color-bg-elevated)] p-4 text-sm whitespace-pre-wrap">
          {detail.evaluationReason}
        </div>
      </div>
    </Card>
  );
}
